
import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, AlertCircle, Gauge, Clock, Info, Hash } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDeviceId, getTooltipStyle } from '@/components/telemetry/utils/chartUtils';

interface DeviceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: {
    id: number;
    device: string;
    issue: string;
    severity: string;
    time: string;
    generatedDeviceId?: string;
  } | null;
  telemetryData?: any[];
}

const DeviceDetailsModal: React.FC<DeviceDetailsModalProps> = ({
  isOpen,
  onClose,
  device,
  telemetryData = []
}) => {
  if (!device) return null;

  // Generate mock time series data for the selected device
  const timeSeriesData = [
    { time: '12:00 AM', value: 230 },
    { time: '2:00 AM', value: 225 },
    { time: '4:00 AM', value: 218 },
    { time: '6:00 AM', value: 232 },
    { time: '8:00 AM', value: 245 },
    { time: '10:00 AM', value: 256 },
    { time: '12:00 PM', value: 258 },
    { time: '2:00 PM', value: 251 },
    { time: '4:00 PM', value: 238 },
    { time: '6:00 PM', value: 242 },
    { time: '8:00 PM', value: 237 },
    { time: '10:00 PM', value: 231 }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'text-energy-red';
      case 'Medium': return 'text-amber-500';
      case 'Low': return 'text-energy-green';
      default: return 'text-gray-500';
    }
  };

  const getSeverityBgColor = (severity: string) => {
    switch (severity) {
      case 'High': return 'bg-red-100 dark:bg-red-900/20';
      case 'Medium': return 'bg-amber-100 dark:bg-amber-900/20';
      case 'Low': return 'bg-green-100 dark:bg-green-900/20';
      default: return 'bg-gray-100 dark:bg-gray-900/20';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'High': return <AlertCircle className="h-5 w-5 text-energy-red" />;
      case 'Medium': return <Activity className="h-5 w-5 text-amber-500" />;
      case 'Low': return <Info className="h-5 w-5 text-energy-green" />;
      default: return <Info className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get device ID - either from generatedDeviceId property or generate from id
  const deviceId = device.generatedDeviceId || `ENERGY-${device.id.toString().padStart(3, '0')}`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex flex-col gap-2">
            <DialogTitle className="flex items-center gap-2">
              {device.device}
              <Badge className={cn("ml-2", getSeverityColor(device.severity))}>
                {device.severity} Severity
              </Badge>
            </DialogTitle>
            <div className="flex items-center text-sm text-muted-foreground">
              <Hash className="h-4 w-4 mr-1" />
              <span className="font-mono">{formatDeviceId(deviceId)}</span>
            </div>
          </div>
          <DialogDescription>
            Reported {device.time} - Issue: {device.issue}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-4">
          <Card className="col-span-1">
            <CardContent className="p-4 flex flex-col items-center justify-center h-full">
              <div className={cn("rounded-full p-3 mb-3", getSeverityBgColor(device.severity))}>
                {getSeverityIcon(device.severity)}
              </div>
              <h3 className="text-lg font-semibold">Status</h3>
              <p className={cn("font-bold", getSeverityColor(device.severity))}>
                {device.severity} Alert
              </p>
            </CardContent>
          </Card>

          <Card className="col-span-1">
            <CardContent className="p-4 flex flex-col items-center justify-center h-full">
              <div className="rounded-full bg-blue-100 p-3 mb-3 dark:bg-blue-900/20">
                <Gauge className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold">Readings</h3>
              <p className="font-bold text-blue-600 dark:text-blue-400">
                {225 + Math.floor(Math.random() * 30)}V
              </p>
            </CardContent>
          </Card>

          <Card className="col-span-1">
            <CardContent className="p-4 flex flex-col items-center justify-center h-full">
              <div className="rounded-full bg-indigo-100 p-3 mb-3 dark:bg-indigo-900/20">
                <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold">Duration</h3>
              <p className="font-bold text-indigo-600 dark:text-indigo-400">
                {Math.floor(Math.random() * 8) + 1} hours
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Device Telemetry - {formatDeviceId(deviceId)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="time" />
                  <YAxis domain={[200, 260]} />
                  <Tooltip 
                    formatter={(value) => [`${value}V`, 'Voltage']}
                    contentStyle={getTooltipStyle()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#FF453A" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" className="mr-2">
            View Device History
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeviceDetailsModal;
